// Solo importamos lo que realmente usamos
// Eliminamos la importación no utilizada de GoogleTagManager
import { useEffect } from 'react';

import { DataLayerArgs, GtagFunction } from '../types/analytics';
import { ANALYTICS_CONFIG } from './analytics-config';

// Declare global gtag function
declare global {
  interface Window {
    gtag: GtagFunction;
    dataLayer: DataLayerArgs[];
    // Tipo específico para propiedades dinámicas de Google Analytics
    [key: `ga-disable-${string}`]: boolean;
  }
}

// Event types for tracking
export type AnalyticsEvent = {
  action: string;
  category: string;
  label?: string;
  value?: number;
};

// Conversion events and funnel stages
export const CONVERSION_EVENTS = {
  // Form submissions
  CONTACT_FORM_SUBMIT: 'contact_form_submit',
  SERVICE_INQUIRY: 'service_inquiry',
  NEWSLETTER_SIGNUP: 'newsletter_signup',
  DOWNLOAD_RESOURCE: 'download_resource',
  CONSULTATION_REQUEST: 'consultation_request',

  // Funnel stages
  VIEW_SERVICE: 'view_service',
  VIEW_PRICING: 'view_pricing',
  START_QUOTE: 'start_quote',
  COMPLETE_QUOTE: 'complete_quote',
  SCHEDULE_DEMO: 'schedule_demo',

  // Email engagement
  EMAIL_SUBSCRIPTION: 'email_subscription',
  EMAIL_OPEN: 'email_open',
  EMAIL_CLICK: 'email_click',
  EMAIL_UNSUBSCRIBE: 'email_unsubscribe',
};

// Track page views
export const trackPageView = (url: string) => {
  try {
    if (typeof window.gtag === 'function') {
      // Using dataLayer to track page views through GTM
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(['event', 'page_view', { page_path: url }] as DataLayerArgs);
    }
  } catch (error) {
    console.error('Error tracking page view:', error);
  }
};

// Track events
export const trackEvent = ({ action, category, label, value }: AnalyticsEvent) => {
  try {
    if (typeof window.gtag === 'function') {
      window.gtag('event', action, {
        event_category: category,
        event_label: label,
        value: value,
      });
    }
  } catch (error) {
    console.error('Error tracking event:', error);
  }
};

// Track conversion with funnel stage
export const trackConversion = (conversionName: string, value?: number, funnelStage?: string) => {
  trackEvent({
    action: conversionName,
    category: 'conversion',
    label: funnelStage,
    value: value,
  });
};

// Track funnel stage
export const trackFunnelStage = (stage: string, metadata?: Record<string, unknown>) => {
  trackEvent({
    action: 'funnel_progress',
    category: 'funnel',
    label: stage,
    value: typeof metadata?.value === 'number' ? metadata.value : undefined,
  });
};

// Track user engagement
export const trackEngagement = (type: string, duration?: number) => {
  trackEvent({
    action: 'user_engagement',
    category: 'engagement',
    label: type,
    value: duration,
  });
};

// Email marketing tracking
export const trackEmailCampaign = (campaignId: string, source: string) => {
  trackEvent({
    action: 'email_campaign_click',
    category: 'email_marketing',
    label: `${campaignId}_${source}`,
  });
};

// Note: Actual analytics initialization is now handled in initializeAnalytics.ts

// Get Google Analytics ID from config
const GA_MEASUREMENT_ID = ANALYTICS_CONFIG.GA_ID;

/**
 * Check if Google Analytics is disabled by consent
 */
export const isGoogleAnalyticsDisabled = (): boolean => {
  try {
    if (typeof window !== 'undefined') {
      return window[`ga-disable-${GA_MEASUREMENT_ID}`] === true;
    }
  } catch (error) {
    console.error('Error checking if Google Analytics is disabled:', error);
  }
  return false;
};

/**
 * Deshabilitar Google Analytics
 */
export const disableGoogleAnalytics = (): void => {
  try {
    if (typeof window !== 'undefined') {
      // Establecer GA como deshabilitado
      window[`ga-disable-${GA_MEASUREMENT_ID}`] = true;

      // Opcionalmente limpiar cookies
      try {
        document.cookie = '_ga=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        document.cookie = '_gid=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        document.cookie = '_gat=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      } catch (cookieError) {
        console.error('Error al limpiar cookies de Google Analytics:', cookieError);
      }
    }
  } catch (error) {
    console.error('Error al deshabilitar Google Analytics:', error);
  }
};

// Custom hook for page view tracking
export const usePageTracking = () => {
  useEffect(() => {
    let isInitialized = false;

    const handleRouteChange = (url: string) => {
      if (!isInitialized) {
        return;
      }
      try {
        trackPageView(url);
      } catch (trackError) {
        console.error('Error tracking page view in handler:', trackError);
      }
    };

    // Track initial page load after a delay to ensure analytics is initialized
    const initTimeout = setTimeout(() => {
      isInitialized = true;
      try {
        handleRouteChange(window.location.pathname);
      } catch (initialTrackError) {
        console.error('Error tracking initial page view:', initialTrackError);
      }
    }, 2000); // Esperar 2 segundos para asegurar que analytics esté inicializado

    // Create a reference to the event handler function
    const popstateHandler = () => {
      try {
        handleRouteChange(window.location.pathname);
      } catch (popstateError) {
        console.error('Error in popstate handler:', popstateError);
      }
    };

    // Add listener for route changes if using client-side routing
    try {
      window.addEventListener('popstate', popstateHandler);
    } catch (addListenerError) {
      console.error('Error adding popstate listener:', addListenerError);
    }

    return () => {
      clearTimeout(initTimeout);
      // Remove the same handler reference
      try {
        window.removeEventListener('popstate', popstateHandler);
      } catch (removeListenerError) {
        console.error('Error removing popstate listener:', removeListenerError);
      }
    };
  }, []); // Empty dependency array
};

export const analytics = {
  trackEvent,
  trackPageView,
  trackConversion,
  trackFunnelStage,
  trackEngagement,
  trackEmailCampaign,
  isGoogleAnalyticsDisabled,
  disableGoogleAnalytics,
};
